/**
 * UI-6a Task 9 — ScheduledReportsPage.
 *
 * Tenant-admin list of scheduled report configs. Composition mirrors
 * `SipTrunksPage`:
 *
 *   - The list is fetched via `useScheduledReports()`, which wraps
 *     `useAdminResource('/reports/scheduled-configs', { responseKey: 'configs' })`.
 *     The backend GET returns `{ configs: [...] }` (UI-6a Task 0a wrap).
 *   - Create / Edit go through `ScheduledReportDrawer` (the drawer owns the
 *     POST/PATCH and reports back via `onSaved`). The page never uses the
 *     hook's `create`/`update`.
 *   - Row Delete opens a `confirmDialog` whose message names the schedule
 *     (`<type> @ <cron>`) and warns that the run history will be deleted
 *     too (cascade via `scheduled_report_runs.config_id ON DELETE CASCADE`).
 *     Accepting issues `DELETE /reports/scheduled-configs/:id` then refetches.
 *   - The Enabled column renders an inline `InputSwitch`; toggling fires
 *     `PATCH /reports/scheduled-configs/:id { enabled }` then refetches.
 *   - After every successful save the page surfaces a transient success
 *     banner (`role="status"`) for ~5s.
 *
 * Two-tier permission model:
 *   - The page wraps in `<RequirePermission permission="reports:view">`. Users
 *     without `reports:view` see the 403 redirect.
 *   - Write actions ("+ New Schedule" button + per-row Edit/Delete) require
 *     `reports:manage`. Without it, the button is hidden and the
 *     ResourceTable is rendered with `actions={[]}` so per-row Edit/Delete
 *     buttons are not rendered either.
 *
 * Backend contract (apps/api/src/routes/reports/scheduled-configs.ts):
 *   GET    /reports/scheduled-configs       -> { configs: ScheduledReportConfig[] }
 *   POST   /reports/scheduled-configs       -> 201 + row (drawer owns)
 *   PATCH  /reports/scheduled-configs/:id   -> 200 + row (drawer owns)
 *   DELETE /reports/scheduled-configs/:id   -> 204
 *
 * Spec: docs/superpowers/specs/2026-05-13-ui-6a-scheduled-reports-design.md §3.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { InputSwitch } from 'primereact/inputswitch'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
  type ResourceTableAction,
} from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import ScheduledReportDrawer from '../../components/admin/reports/ScheduledReportDrawer.js'
import {
  useScheduledReports,
  type ScheduledReportConfig,
} from '../../hooks/useScheduledReports.js'
import { useAuthStore } from '../../store/authStore.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatNextRun } from '../../utils/cron-preview.js'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Recipient emails column truncates after this many shown chips, with "+N more". */
const RECIPIENTS_VISIBLE = 3

/** Duration (ms) the post-save success banner stays visible. */
const SUCCESS_BANNER_MS = 5000

// Stable module-level fallback so the auth-store selector doesn't return a
// fresh array each render (which would trigger a re-render loop).
const EMPTY_PERMS: string[] = []

// ---------------------------------------------------------------------------
// Type / Format tag color helpers
// ---------------------------------------------------------------------------

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | undefined

/**
 * Maps a report type to a Tag severity. The colors are arbitrary but stable
 * — picked so each of the 4 currently supported types gets a distinct color.
 */
function reportTypeSeverity(type: string): TagSeverity {
  switch (type) {
    case 'sla':
      return 'warning'
    case 'csat':
      return 'success'
    case 'agent-performance':
      return 'info'
    case 'call-volume':
    default:
      return undefined
  }
}

/**
 * Maps a delivery format to a Tag severity. CSV is the default (no color),
 * Excel is `info`, PDF is `danger` (red). Choices are conventional — the
 * Tag's `value` is the uppercase format string for readability.
 */
function formatSeverity(format: string): TagSeverity {
  switch (format) {
    case 'excel':
      return 'info'
    case 'pdf':
      return 'danger'
    case 'csv':
    default:
      return undefined
  }
}

/**
 * Renders the recipients list: up to RECIPIENTS_VISIBLE emails joined by
 * ", ", then a "+N more" suffix when there are extras. Empty arrays render
 * as an em-dash placeholder.
 */
function formatRecipients(emails: string[]): string {
  if (emails.length === 0) return '—'
  if (emails.length <= RECIPIENTS_VISIBLE) return emails.join(', ')
  const head = emails.slice(0, RECIPIENTS_VISIBLE).join(', ')
  const rest = emails.length - RECIPIENTS_VISIBLE
  return `${head} +${rest} more`
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ScheduledReportsPage(): JSX.Element {
  const toast = useToast()

  const {
    items: configs,
    loading,
    error,
    refetch,
    pagination,
  } = useScheduledReports()

  // --- Permissions ---------------------------------------------------------
  // Two-tier: page on `reports:view`; write actions on `reports:manage`.
  const permissions = useAuthStore(
    (s) => s.user?.permissions ?? EMPTY_PERMS,
  )
  const canManage = permissions.includes('reports:manage')

  // --- Drawer state --------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<ScheduledReportConfig | null>(null)

  // --- Success banner (after save/delete/toggle) ---------------------------
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
    }
  }, [])

  function flashSuccess(message: string): void {
    setSuccessMessage(message)
    if (successTimerRef.current) clearTimeout(successTimerRef.current)
    successTimerRef.current = setTimeout(() => {
      setSuccessMessage(null)
      successTimerRef.current = null
    }, SUCCESS_BANNER_MS)
  }

  // --- Delete flow ---------------------------------------------------------
  function handleDelete(c: ScheduledReportConfig): void {
    const label = `${c.reportType} @ ${c.cronExpression}`
    confirmDialog({
      message: `Permanently delete the "${label}" schedule? Its run history will also be deleted.`,
      header: 'Delete Scheduled Report',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/reports/scheduled-configs/${c.id}`)
          toast.success('Scheduled report deleted')
          flashSuccess('Scheduled report deleted.')
          refetch()
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : 'Failed to delete scheduled report',
          )
        }
      },
    })
  }

  // --- Enabled toggle ------------------------------------------------------
  async function handleToggleEnabled(c: ScheduledReportConfig): Promise<void> {
    try {
      await api.patch(`/reports/scheduled-configs/${c.id}`, {
        enabled: !c.enabled,
      })
      refetch()
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to update scheduled report',
      )
      refetch()
    }
  }

  function enabledCell(c: ScheduledReportConfig): JSX.Element {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <InputSwitch
          checked={c.enabled}
          aria-label={`Enabled: ${c.reportType} @ ${c.cronExpression}`}
          onChange={(e) => {
            void handleToggleEnabled(c)
            void e
          }}
        />
      </div>
    )
  }

  // --- Columns -------------------------------------------------------------
  const columns: ColumnDef<ScheduledReportConfig>[] = [
    {
      label: 'Name',
      accessor: (c) => `${c.reportType} @ ${c.cronExpression}`,
    },
    {
      label: 'Type',
      accessor: (c) => (
        <Tag value={c.reportType} severity={reportTypeSeverity(c.reportType)} />
      ),
    },
    { label: 'Schedule', accessor: (c) => c.cronExpression },
    { label: 'Recipients', accessor: (c) => formatRecipients(c.recipientEmails) },
    {
      label: 'Format',
      accessor: (c) => (
        <Tag value={c.format.toUpperCase()} severity={formatSeverity(c.format)} />
      ),
    },
    {
      label: 'Next Run',
      accessor: (c) => formatNextRun(c.cronExpression),
    },
    { label: 'Enabled', accessor: (c) => enabledCell(c) },
  ]

  // canManage gates per-row actions: empty array suppresses the trailing
  // Actions column entirely so viewers don't see disabled / non-functional
  // buttons.
  const tableActions: ResourceTableAction[] = canManage ? ['edit', 'delete'] : []

  return (
    <RequirePermission permission="reports:view">
      <div className="space-y-4 p-4">
        <PageHeader
          title="Scheduled Reports"
          description="Recurring reports delivered by email on a cron schedule."
          actions={
            canManage ? (
              <Button
                label="+ New Schedule"
                onClick={() => {
                  setEditing(null)
                  setDrawerOpen(true)
                }}
              />
            ) : null
          }
        />

        {error && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="underline"
            >
              Retry
            </button>
          </div>
        )}

        {successMessage && (
          <div
            role="status"
            className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          >
            {successMessage}
          </div>
        )}

        <ResourceTable<ScheduledReportConfig>
          columns={columns}
          rows={configs}
          loading={loading}
          actions={tableActions}
          onEdit={(c) => {
            setEditing(c)
            setDrawerOpen(true)
          }}
          onDelete={handleDelete}
          empty={{
            title: 'No scheduled reports',
            description:
              'Create a scheduled report to deliver call-volume, SLA, CSAT, or agent-performance summaries by email.',
            icon: 'pi pi-calendar',
          }}
          pagination={pagination}
        />

        <ScheduledReportDrawer
          open={drawerOpen}
          config={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            refetch()
            flashSuccess(
              editing ? 'Scheduled report updated.' : 'Scheduled report created.',
            )
            setDrawerOpen(false)
          }}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
